import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import Login from './Login';
import SignUp from './SignUp';
import Llama from './Llama';
import Guide from './guide';
import SignOut from './SignOut';
import ProfileBuilder from './ProfileBuilder';
import Profile from './Profile';
import Rankings from './Rankings';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';

// ── Error Boundary ─────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <p>{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ error: null }); window.location.href = '/home'; }}>
            Back to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Protected Route ────────────────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'auth' | 'unauth'

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), user => {
      setStatus(user ? 'auth' : 'unauth');
    });
    return unsub;
  }, []);

  if (status === 'loading') return <div className="route-loading"><div className="route-spinner" /></div>;
  if (status === 'unauth') return <Navigate to="/login" replace />;
  return children;
}

// ── Home (redirects logged-in users) ──────────────────────────────────────────
function Home() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn]   = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), user => {
      setIsLoggedIn(!!user);
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  if (!authChecked) return null;
  if (isLoggedIn) return <Navigate to="/home" replace />;

  return (
    <div className="home-content">
      <div className="home-hero">
        <h1>Don't waste your time and money. Discover your college in the United States and build your profile without spending a penny.</h1>
        <div className="home-hero-btns">
          <Link to="/sign-up"><button className="btn-primary">Get Started</button></Link>
          <Link to="/login"><button className="btn-outline">Sign In</button></Link>
        </div>
      </div>

      <div className="home-section fade-in">
        <div className="home-section-inner">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <span className="feature-icon">🎯</span>
              <p>AI-powered profile builder tailored to your home country</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">🏫</span>
              <p>Find your dream college from thousands of US universities</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">🌍</span>
              <p>Understand exactly what American universities want from international students</p>
            </div>
          </div>
        </div>
      </div>

      <div className="home-section home-section--alt fade-in">
        <div className="home-section-inner">
          <h2>Our Vision</h2>
          <p className="home-section-text">
            Our vision is to inform international students wishing to study in the United States
            about what colleges want from you and to help international students build their profiles
            according to their choice of major and then choose a college of their choice without
            spending a penny.
          </p>
        </div>
      </div>

      <div className="home-section home-section--hackathon fade-in">
        <div className="home-section-inner home-section-inner--center">
          <h2>🏆 8th Place at Bridging the Gap Hackathon</h2>
          <a href="https://devpost.com/software/par-videsh" target="_blank" rel="noopener noreferrer">
            <img src="/imagedog.jpeg" alt="8th Place at Bridging the Gap Hackathon" className="hackathon-img" />
          </a>
        </div>
      </div>

      <div className="home-section fade-in">
        <div className="home-section-inner">
          <h2>What students say</h2>
          <div className="testimonials-grid">
            {[
              { quote: "Excellent website and very resourceful!", author: "Aneesh Sharma" },
              { quote: "Great AI to guide you through shaping your college profile", author: "Harshal Dhaduk" },
              { quote: "It's a pretty good app", author: "Atharv Gupta" },
              { quote: "It's an overall good and versatile app. It is useful, it gives good feedback and the tools are intuitive.", author: "Tattva Jivani" },
              { quote: "Very informative and helpful", author: "Manya Mahajan" },
              { quote: "As an international student, this gave me a solid understanding of how admissions work in the United States.", author: "Arnav Kinra" },
              { quote: "This website is a great tool for navigating the confusing college application process", author: "Tanish Dasari" },
            ].map(({ quote, author }) => (
              <div key={author} className="testimonial-card">
                <p className="stars">⭐⭐⭐⭐⭐</p>
                <p className="quote">"{quote}"</p>
                <p className="author">— {author}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Theme toggle ───────────────────────────────────────────────────────────────
const THEME_CYCLE = { auto: 'light', light: 'dark', dark: 'auto' };
const THEME_ICON  = { auto: 'fa-circle-half-stroke', light: 'fa-sun', dark: 'fa-moon' };
const THEME_LABEL = { auto: 'Auto', light: 'Light', dark: 'Dark' };

function ThemeToggle() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'auto');

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'auto') {
      root.removeAttribute('data-theme');
      localStorage.removeItem('theme');
    } else {
      root.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  return (
    <button
      className="theme-toggle"
      onClick={() => setTheme(t => THEME_CYCLE[t])}
      title={`Theme: ${THEME_LABEL[theme]} — click to change`}
      aria-label={`Current theme: ${THEME_LABEL[theme]}`}
    >
      <i className={`fa-solid ${THEME_ICON[theme]}`} />
      <span className="theme-label">{THEME_LABEL[theme]}</span>
    </button>
  );
}

// ── User menu (avatar + sign-out dropdown) ─────────────────────────────────────
function UserMenu() {
  const [user, setUser]       = useState(null);
  const [open, setOpen]       = useState(false);
  const ref                   = useRef(null);
  const navigate              = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), u => setUser(u));
    return unsub;
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user) return null;

  const initials = (user.displayName || user.email || '?')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const handleSignOut = async () => {
    await getAuth().signOut();
    navigate('/login');
  };

  return (
    <div className="user-menu" ref={ref}>
      <button className="user-avatar" onClick={() => setOpen(o => !o)} aria-label="Account menu">
        {user.photoURL
          ? <img src={user.photoURL} alt="avatar" className="user-avatar-img" />
          : <span className="user-avatar-initials">{initials}</span>}
      </button>
      {open && (
        <div className="user-dropdown">
          <div className="user-dropdown-info">
            <span className="user-dropdown-name">{user.displayName || 'Student'}</span>
            <span className="user-dropdown-email">{user.email}</span>
          </div>
          <hr className="user-dropdown-divider" />
          <button className="user-dropdown-item" onClick={handleSignOut}>
            <i className="fa-solid fa-right-from-bracket" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

// ── App ────────────────────────────────────────────────────────────────────────
function App() {
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.fade-in');
      const triggerPoint = window.innerHeight * 0.85;
      sections.forEach(section => {
        if (section.getBoundingClientRect().top < triggerPoint) {
          section.classList.add('show');
        } else {
          section.classList.remove('show');
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Router>
      <div className="App">
        <header className="App-header">
          <div className="header-content">
            <Link to="/">
              <img src="/favicon.ico" alt="Reverieon AI" className="header-logo" />
            </Link>
            <h1 className="header-title">Reverieon AI</h1>
          </div>
          <div className="header-right">
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <ErrorBoundary>
          <Routes>
            <Route path="/"               element={<Home />} />
            <Route path="/login"          element={<Login />} />
            <Route path="/sign-up"        element={<SignUp />} />
            <Route path="/how"            element={<Guide />} />
            <Route path="/sign-out"       element={<SignOut />} />
            <Route path="/home"           element={<ProtectedRoute><Llama /></ProtectedRoute>} />
            <Route path="/profile-builder" element={<ProtectedRoute><ProfileBuilder /></ProtectedRoute>} />
            <Route path="/criteria"       element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/rankings"       element={<ProtectedRoute><Rankings /></ProtectedRoute>} />
            <Route path="*"               element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>

        <footer className="App-footer">
          <div className="footer-inner">
            <img src="/favicon.ico" alt="Reverieon AI" className="footer-logo" />
            <p className="footer-copy">&copy; {new Date().getFullYear()} Rishit Seth. All rights reserved</p>
            <p className="footer-privacy">
              This tool is for educational guidance only. Profile data you enter is stored locally in your browser and sent to Google Gemini and OpenAI to generate analysis. It is not stored on our servers. Always verify information with official university websites and a licensed counselor.
            </p>
            <div className="footer-icons">
              <a href="https://www.linkedin.com/company/reverieon-ai" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fab fa-linkedin" />
              </a>
              <a href="https://www.instagram.com/reverieon.ai" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fab fa-instagram" />
              </a>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
